<template>
    <div class="container input-container address-field-container">
        <div v-if="!manualAddressStep">
            <InputSearchField
                :disabled="false"
                :editMessage="editMessage"
                :customErrorMessage="addressCustomErrorMessage"
                :label="addressLabel"
                :manualInput="manualAddressStep"
                :manualMessage="manualMessage"
                :name="searchName"
                :vuexKey="vuexKey"
                :searchUrl="
                    includeTerritories('usa') ? url('/api/postcode/all-items-list') : url('/api/postcode/address-list')
                "
                :postMethod="true"
                :selectedName="name"
                :validationLabel="label"
                :validationRules="validationRules"
                :searchType="searchType"
                :ref="searchName"
            />
        </div>

        <div v-else-if="includeTerritories('usa')">
            <InputField
                class="address-field"
                :class="['StreetAddress']"
                :key="['StreetAddress']"
                :customErrorMessage="addressCustomErrorMessage"
                :validationRules="validationRules"
                :name="searchName"
                :vuexKey="vuexKey"
                :label="addressLabel"
                :ref="searchName"
            />
        </div>

        <div
            v-if="manualAddressStep"
            v-for="(addressFieldArr, arrKey, index) in manualFieldsLayout"
            class="row no-gutters"
        >
            <div v-for="addressField in addressFieldArr" :class="addressField?.class || 'col'">
                <AutocompleteField
                    v-if="autoCompleteInput(addressField) && includeTerritories('usa', 'aus')"
                    :name="addressField.name"
                    :ref="addressField.name"
                    :vuexKey="vuexKey"
                    :validationRules="addressField.validationRules"
                    :customErrorMessage="addressField.errorMessage"
                    :label="addressField.label"
                    :limit="8"
                    :options="states"
                    :noResultsErrorMessage="$t('form.fields.states.noResultsErrorMessage')"
                    class="address-field"
                />

                <InputField
                    v-else-if="!autoCompleteInput(addressField)"
                    class="address-field"
                    :class="index"
                    :key="index"
                    :customErrorMessage="addressField.errorMessage"
                    :validationRules="addressField.validationRules"
                    :name="addressField.name"
                    :vuexKey="vuexKey"
                    :label="addressField.label"
                    :ref="addressField.name"
                />
            </div>
        </div>

        <div v-if="!manualAddressStep">
            <div
                :key="addressFieldIndex"
                :rules="addressField.validationRules"
                v-for="(addressField, addressFieldIndex) in manualFields"
            >
                <Field type="hidden" :name="addressField.name" v-model="addressField.validationValue" />
                <div class="error" v-if="errorMessage">{{ customErrorMessage || errorMessage }}</div>
            </div>
        </div>

        <div class="error" v-if="networkError">{{ networkError }}</div>

        <MessageButton v-if="manualAddressStep && this.step?.supportsSearch" @click="showManualFields(false)">
            {{ searchAgainMessage }}
        </MessageButton>
    </div>
</template>

<script>
import { useStore } from 'vuex'
import { useField, Field } from 'vee-validate'
import { networkErrorMessage } from '@js/utilities/error.js'

// lodash components
import filter from 'lodash/filter'
import forEach from 'lodash/forEach'
import map from 'lodash/map'

export default {
    components: {
        Field,
    },
    props: {
        errorMessage: String,
        customErrorMessage: String,
        label: String,
        name: String,
        addressType: String,
        searchName: String,
        searchLabel: String,
        vuexKey: String,
        searchType: {
            type: String,
            default: 'standard',
        },
        addressFields: {
            default: () => {},
        },
        validationRules: {
            default: () => {},
        },
    },
    data() {
        return {
            loading: false,
            manualMessage: this.$t('form.fields.address.manualMessage'),
            editMessage: this.$t('form.fields.address.editMessage'),
            searchAgainMessage: this.$t('form.fields.address.searchAgainMessage'),
            networkError: null,
            usedFields: [],
        }
    },
    setup(props) {
        const store = useStore()

        return {
            store,
        }
    },
    created() {
        this.emitter.all.delete('searchField:selected')
        this.emitter.on('searchField:selected', payload => {
            this.addressSelected(payload)
        })

        this.emitter.all.delete('searchField:showManualFields')
        this.emitter.on('searchField:showManualFields', payload => {
            this.showManualFields(payload.manual, payload.keepValues)
        })
    },
    async mounted() {
        if (this.hasAnyFilledManualFields || this.hasManualFieldsWithServerError) {
            this.manualAddressStep = true
        }

        if (this.manualAddressStep) {
            this.setupManualFieldsValidation()
        }

        this.focusPriorityInput()

        this.updateContinue()
    },
    computed: {
        value: {
            get() {
                return this.step.selected
            },
            set(value) {
                this.step.selected = value
            },
        },
        manualFields: function () {
            return filter(this.addressFields, field => {
                return !field?.searchField
            })
        },
        manualFieldsLayout: function () {
            const fieldLayout = []
            let lastFieldIndex = -1
            this.manualFields.forEach(field => {
                if (field.sameLineAsLast && lastFieldIndex >= 0) {
                    fieldLayout[lastFieldIndex].push(field)
                } else {
                    fieldLayout.push([field])
                    lastFieldIndex++
                }
            })

            return fieldLayout
        },
        hasManualFieldsWithServerError: function () {
            let foundFieldWithError = false
            forEach(this.manualFields, field => {
                if (this.store.getters.fieldServerError(field.name)) {
                    foundFieldWithError = true
                    return false
                }

                return true
            })

            return foundFieldWithError
        },
        manualFieldOutstandingRequired: async function () {
            let hasErrors = false

            for (const key in this.usedFields) {
                if (Object.hasOwnProperty.call(this.usedFields, key)) {
                    const validateResult = await this.usedFields[key].validate()
                    if (!validateResult.valid) {
                        hasErrors = true
                    }
                }
            }

            return hasErrors
        },
        hasAnyFilledManualFields() {
            for (const key in this.step?.fields ?? {}) {
                if (Object.hasOwnProperty.call(this.step.fields, key)) {
                    const field = this.step.fields[key]
                    if (field.value) {
                        return true
                    }
                }
            }

            return false
        },
        showOverideMessage: function () {
            return this.manualMessage && !this.searching && this.options.length ? true : false
        },
        buildingNameValue: function () {
            if (process.env.TERRITORY !== 'usa' && this.addressFields?.BuildingName?.name) {
                return this.store.getters.fieldValue(
                    this.addressFields.BuildingName.name,
                    this.vuexKey || 'data',
                    !!this.vuexKey,
                )
            }

            return null
        },
        buildingNumberValue: function () {
            if (this.addressFields?.BuildingNumber?.name) {
                return this.store.getters.fieldValue(
                    this.addressFields.BuildingNumber.name,
                    this.vuexKey || 'data',
                    !!this.vuexKey,
                )
            }

            return null
        },
        manualAddressStep: {
            get() {
                return this.step.manual
            },
            set(manual) {
                this.step.manual = manual
            },
        },
        states: function () {
            const states = this.$i18n.messages[this.$i18n.locale].form.fields?.states?.options
            if (states && states.length) {
                return map(states, state => {
                    return {
                        id: state.value,
                        title: state.name,
                        ref: state.value,
                    }
                })
            }

            return []
        },
        addressLabel: function () {
            if (this.includeTerritories('usa')) {
                if (this.manualAddressStep) {
                    return this.addressFields['StreetAddress'].selectLabel
                } else {
                    return this.addressFields['StreetAddress'].label
                }
            }

            return this.searchLabel
        },
        addressCustomErrorMessage: function () {
            if (this.includeTerritories('usa')) {
                return this.addressFields['StreetAddress'].errorMessage
            }

            return this.customErrorMessage
        },
        step() {
            return this.store.state.apply.currentRouteStep
        },
    },
    watch: {
        buildingNameValue: function (value) {
            this.resetFieldByName(this.addressFields.BuildingName.name, value, true)
            this.resetFieldByName(this.addressFields.BuildingNumber.name)
        },
        buildingNumberValue: function (value) {
            if (process.env.TERRITORY !== 'usa') {
                this.resetFieldByName(this.addressFields.BuildingName.name)
                this.resetFieldByName(this.addressFields.BuildingNumber.name, value, true)
            }
        },
    },
    methods: {
        setupManualFieldsValidation() {
            if (this.usedFields.length) {
                return
            }

            for (const key in this.addressFields) {
                const field = this.addressFields[key]

                if (!field?.searchField) {
                    const { validate, resetField } = useField(field.name, field.validationRules, {
                        initialValue: field.value,
                    })

                    this.usedFields.push({
                        name: field.name,
                        validate,
                        resetField,
                    })
                }
            }
        },
        resetFieldByName(name, value, doResetValue = false) {
            const field = this.usedFields.find(usedField => {
                return usedField.name == name
            })

            if (field && doResetValue) {
                field.resetField({ value })
            }
        },
        updateContinue: function () {
            this.store.dispatch('updateStepSetting', {
                setting: 'hideContinue',
                settingValue: !this.manualAddressStep,
                step: this.store.state.apply.currentRouteStep,
            })
        },
        showManualFields: function (manual, keepValues = false) {
            let goingBackToSearch = false
            const manualAddressStep = this.step?.supportsSearch ? manual : true

            if (this.manualAddressStep !== manualAddressStep) {
                goingBackToSearch = !manual && this.manualAddressStep
                this.manualAddressStep = manualAddressStep
                this.updateContinue()
            }

            if (this.manualAddressStep) {
                this.setupManualFieldsValidation()
            }

            if (!keepValues) {
                if (goingBackToSearch) {
                    this.clearSelectedInputOption()
                }

                for (let key in this.manualFields) {
                    this.addressInput({
                        name: this.manualFields[key].name.split(`${this.name}`)[1],
                        value: null,
                    })
                }
            }

            this.focusPriorityInput()
        },
        addressSelected: function (option) {
            this.value = option.value
            this.getAddressDetails(option)
        },
        getAddressDetails: async function (option) {
            try {
                this.networkError = null

                const id = encodeURIComponent(encodeURIComponent(option.value.Id))
                const response = await axios.get(this.url('/api/postcode/address/') + id)

                if (response && response.data) {
                    const validatedAddress = response.data.Items[0]
                    let street = validatedAddress.Street

                    if (validatedAddress.SecondaryStreet) {
                        street = street.concat(', ', validatedAddress.SecondaryStreet)
                    }

                    let addressInputs = {}

                    if (process.env.TERRITORY !== 'usa') {
                        addressInputs = {
                            Postcode: validatedAddress.PostalCode,
                            BuildingName: validatedAddress.BuildingName,
                            BuildingNumber: validatedAddress.BuildingNumber,
                            StreetName: street,
                            City: validatedAddress.City,
                            County: validatedAddress.Province || validatedAddress.AdminAreaName,
                        }
                    } else {
                        addressInputs = {
                            ZipCode: validatedAddress.PostalCode.substr(
                                0,
                                this.fieldMaxLength(`${this.addressType}AddressZipCode`),
                            ),
                            StreetAddress: `${validatedAddress.BuildingNumber} ${street}`,
                            BuildingNumber: validatedAddress.SubBuilding,
                            State: validatedAddress.ProvinceCode,
                            City: validatedAddress.City,
                        }
                    }

                    for (const name in addressInputs) {
                        const value = addressInputs[name]
                        await this.addressInput({
                            name: name,
                            value: value,
                        })
                    }

                    this.showManualFields(true, true)
                }
            } catch (exception) {
                this.networkError = networkErrorMessage(this.store, exception)

                if (exception?.name != 'NetworkError') {
                    throw exception
                }
            }
        },
        addressInput: async function (data) {
            await this.store.dispatch('setFieldValue', {
                name: `${this.name}${data.name}`,
                value: data.value,
            })

            if (
                this.includeTerritories('usa') &&
                'AutoCompleteField' in this.addressFields[data.name] &&
                this.addressFields[data.name].AutoCompleteField
            ) {
                await this.emitter.emit('selectResult', {
                    id: data.value,
                    ref: data.value,
                })
            }
        },
        fieldMaxLength: function (fieldName) {
            const fieldValidation = this.store.getters.fieldValidationRules(fieldName)

            return fieldValidation.max || 0
        },
        focusPriorityInput() {
            this.$nextTick(() => {
                if (this.manualAddressStep) {
                    this.autoFocus(this.$refs[this.manualFields[0].name][0])
                } else {
                    this.autoFocus(this.$refs[this.searchName])
                }
            })
        },
        autoCompleteInput: field => 'AutoCompleteField' in field && field.AutoCompleteField,
        clearSelectedInputOption() {
            this.store.state.apply.currentRouteStep.selected = null
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~scss/settings/responsive';
@import '~scss/mixins/rem';

.input-container {
    .address-field {
        margin-bottom: 20px;
    }
}

.error {
    position: relative;
    padding: 5px 5px 5px 22px;
    font-size: rem(11px);
    color: #ff0000;

    .icon {
        position: absolute;
        left: 2px;
        top: 6px;
        font-size: rem(14px);
    }
}
</style>
